import React from "react";
import { Button, Icon, StatusBadge } from "../../components/tailwind";
import type { StatusBadgeColor } from "../../components/tailwind";
import { Spinner } from "../../utils/Spinner";
import { EnrichFieldRenderer } from "./EnrichFieldRenderer";
import { EnrichedField, EnrichStatus } from "../../api/enrichApi";

const getStatusBadgeColor = (status: EnrichStatus): StatusBadgeColor => {
  switch (status) {
    case EnrichStatus.NOT_ENRICHED:
      return "yellow";
    case EnrichStatus.ENRICHED:
      return "green";
    case EnrichStatus.ENRICHED_AND_SENT_TO_PIM:
      return "blue";
    case EnrichStatus.FAILED_TO_SEND_TO_PIM:
      return "red";
    default:
      return "gray";
  }
};

type Props = {
  field: EnrichedField;
  isEnrichingField: boolean;
  disabled: boolean;
  onSave: (id: number, value: any) => Promise<void>;
  onRevert: (id: number) => void;
  onEnrichField: () => void;
  onEditField: (customerFieldId: number) => void;
};

export const EnrichResultRow: React.FC<Props> = ({
  field,
  isEnrichingField,
  disabled,
  onSave,
  onRevert,
  onEnrichField,
  onEditField,
}) => {
  const isDisabled = disabled || isEnrichingField;

  let statusLabel: string;
  let statusColor: StatusBadgeColor;
  let statusTooltip: string | undefined;

  if (field.send_error) {
    statusLabel = "Send to PIM Failed";
    statusColor = getStatusBadgeColor(EnrichStatus.FAILED_TO_SEND_TO_PIM);
    statusTooltip = field.send_error;
  } else if (field.published) {
    statusLabel = "Sent to PIM";
    statusColor = getStatusBadgeColor(EnrichStatus.ENRICHED_AND_SENT_TO_PIM);
    statusTooltip = new Date(field.published).toLocaleString();
  } else {
    statusLabel = "Enriched";
    statusColor = getStatusBadgeColor(EnrichStatus.ENRICHED);
  }

  return (
    <tr
      className={`hover:tw-bg-gray-50 ${
        isEnrichingField ? "tw-opacity-60" : ""
      }`}
    >
      <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-medium tw-text-gray-900">
        <span className="tw-truncate">{field.display_key}</span>
      </td>
      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-900">
        <EnrichFieldRenderer
          field={field}
          onSave={onSave}
          disabled={isDisabled}
        />
      </td>
      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-600">
        {field.reasoning || "-"}
      </td>
      <td className="tw-px-6 tw-py-4 tw-text-sm">
        <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start">
          <StatusBadge
            label={statusLabel}
            color={statusColor}
            tooltip={statusTooltip}
            tooltipPos="left"
          />
          {field.is_edited && <StatusBadge label="Edited" color="violet" />}
          {field.existing_pim_value && !field.published && (
            <StatusBadge
              label="Has existing PIM Value"
              color="amber"
              tooltip={`Current PIM value: ${field.existing_pim_value}`}
              tooltipPos="left"
            />
          )}
        </div>
      </td>
      <td className="tw-px-6 tw-py-4 tw-text-sm">
        {isEnrichingField ? (
          <Spinner />
        ) : (
          <div className="tw-flex tw-items-center tw-gap-1">
            {field.is_edited && (
              <Button
                onClick={() => onRevert(field.id)}
                disabled={isDisabled}
                className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                title="Revert to original AI value"
                size="sm"
              >
                <Icon name="undo" />
              </Button>
            )}
            {field.customer_field_id && (
              <Button
                onClick={onEnrichField}
                disabled={isDisabled}
                className="tw-p-1 !tw-border-textual-blue !tw-text-textual-blue hover:!tw-bg-textual-blue hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed "
                title="Enrich again"
                size="sm"
              >
                <Icon name="auto_fix_high" className="tw-text-base" />
              </Button>
            )}
            {field.customer_field_id && (
              <Button
                onClick={() => onEditField(field.customer_field_id)}
                disabled={isDisabled}
                className="tw-p-1 tw-text-gray-500 hover:!tw-brightness-100 hover:!tw-text-white hover:!tw-shadow-sm !tw-border-gray-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                title="Edit field configuration"
                size="sm"
              >
                <Icon name="settings" className="tw-text-base" />
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};
